import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from '@mui/material';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
  Upload as UploadIcon,
  Work as WorkIcon,
  QuestionAnswer as QuestionIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import apiService from '../services/api';

const CandidateMatchingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();
  const { config } = useConfig();
  
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(searchParams.get('jobId') || '');
  const [selectedJob, setSelectedJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [error, setError] = useState('');
  const [interviewQuestions, setInterviewQuestions] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState({});
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [questionsDialogOpen, setQuestionsDialogOpen] = useState(false);
  const [selectedCandidateQuestions, setSelectedCandidateQuestions] = useState(null);

  useEffect(() => {
    if (config && getAuthToken) {
      apiService.initialize(config.apiGatewayUrl, getAuthToken);
      loadJobs();
    }
  }, [config, getAuthToken]);

  useEffect(() => {
    if (selectedJobId && jobs.length > 0) {
      const job = jobs.find(j => j.jobId === selectedJobId);
      setSelectedJob(job);
      loadResumesForJob(selectedJobId);
      loadCandidatesForJob(selectedJobId); // Add this line to load candidates immediately
    }
  }, [selectedJobId, jobs]);

  const loadJobs = async () => {
    try {
      setJobsLoading(true);
      const jobsData = await apiService.getJobs();
      setJobs(jobsData);
    } catch (err) {
      setError('Failed to load jobs');
      console.error('Jobs loading error:', err);
    } finally {
      setJobsLoading(false);
    }
  };

  const loadResumesForJob = async (jobId) => {
    try {
      const resumesData = await apiService.getResumesForJob(jobId);
      setResumes(resumesData);
    } catch (err) {
      console.error('Error loading resumes:', err);
      setResumes([]);
    }
  };

  const loadCandidatesForJob = async (jobId) => {
    try {
      // Load candidates from DynamoDB without running full matching
      const results = await apiService.getCandidateMatches(jobId);
      if (results.matches && results.matches.length > 0) {
        // Map the API response to match the frontend's expected structure
        const mappedCandidates = results.matches.map(candidate => ({
          ...candidate,
          matchScore: candidate.analysis?.score || 0,
          fileName: candidate.fileName || `${candidate.name}.pdf`,
          recommendation: candidate.analysis?.recommendation || 'No recommendation available',
          // Ensure all DynamoDB fields are properly mapped
          skills: candidate.skills || [],
          email: candidate.email || '',
          phone: candidate.phone || '',
          location: candidate.location || '',
          experience_years: candidate.experience_years || 0,
          summary: candidate.summary || '',
          work_experience: candidate.work_experience || [],
          education: candidate.education || []
        }));
        setCandidates(mappedCandidates);
      } else {
        setCandidates([]);
      }
    } catch (err) {
      console.error('Error loading candidates:', err);
      setCandidates([]);
    }
  };

  const handleJobChange = (event) => {
    const newJobId = event.target.value;
    setSelectedJobId(newJobId);
    setCandidates([]);
    setResumes([]);
    setError('');
  };

  const handleViewDetails = (candidate) => {
    setSelectedCandidate(candidate);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedCandidate(null);
  };

  const handleCloseQuestions = () => {
    setQuestionsDialogOpen(false);
    setSelectedCandidateQuestions(null);
  };

  const handleCopyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log(`${fieldName} copied to clipboard`);
    });
  };

  const CopyableField = ({ label, value, multiline = false }) => (
    <Box mb={2}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Box display="flex" alignItems={multiline ? "flex-start" : "center"} gap={1}>
        <TextField
          value={value || 'Not available'}
          variant="outlined"
          size="small"
          fullWidth
          multiline={multiline}
          rows={multiline ? 3 : 1}
          InputProps={{
            readOnly: true,
          }}
          sx={{ 
            '& .MuiInputBase-input': { 
              fontSize: '0.875rem',
              cursor: 'text'
            }
          }}
        />
        {value && (
          <IconButton 
            size="small" 
            onClick={() => handleCopyToClipboard(value, label)}
            title={`Copy ${label}`}
          >
            <CopyIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  const QuestionCard = ({ question, index, category }) => (
    <Card sx={{ mb: 2 }} variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
            {category} Question {index + 1}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => handleCopyToClipboard(question.question, `${category} Question ${index + 1}`)}
            title="Copy question"
          >
            <CopyIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {question.question}
        </Typography>
        <Chip 
          label={question.focus} 
          size="small" 
          variant="outlined" 
          color="secondary"
        />
      </CardContent>
    </Card>
  );

  const findMatches = async () => {
    if (!selectedJobId) {
      setError('Please select a job first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use the DynamoDB-based matching API that reads from job candidates list
      const results = await apiService.getCandidateMatches(selectedJobId);
      
      if (results.totalCandidates === 0) {
        setError(results.message || 'No candidates found for this job. Please upload resumes first.');
        setCandidates([]);
      } else {
        // Map the API response to match the frontend's expected structure
        const mappedCandidates = (results.matches || []).map(candidate => ({
          ...candidate,
          matchScore: candidate.analysis?.score || 0,
          fileName: candidate.fileName || `${candidate.name}.pdf`,
          recommendation: candidate.analysis?.recommendation || 'No recommendation available',
          // Ensure all DynamoDB fields are properly mapped
          skills: candidate.skills || [],
          email: candidate.email || '',
          phone: candidate.phone || '',
          location: candidate.location || '',
          experience_years: candidate.experience_years || 0,
          summary: candidate.summary || '',
          work_experience: candidate.work_experience || [],
          education: candidate.education || []
        }));
        setCandidates(mappedCandidates);
      }
      
    } catch (err) {
      setError(err.message || 'Failed to find candidate matches');
    } finally {
      setLoading(false);
    }
  };

  const handleManageResumes = () => {
    navigate(`/resume-management?jobId=${selectedJobId}`);
  };

  const handleGenerateQuestions = async (candidateId, candidateName) => {
    console.log('Generate questions clicked:', { candidateId, candidateName, selectedJobId });
    
    if (!selectedJobId) {
      setError('Please select a job first');
      return;
    }

    if (!candidateId) {
      setError('Candidate ID is missing');
      return;
    }

    setLoadingQuestions(prev => ({ ...prev, [candidateId]: true }));
    setError('');

    try {
      console.log('Calling API with:', { jobId: selectedJobId, candidateId });
      const questions = await apiService.generateInterviewQuestions(selectedJobId, candidateId);
      console.log('Questions received:', questions);
      setInterviewQuestions(prev => ({ ...prev, [candidateId]: questions }));
      
      // Open the questions modal
      setSelectedCandidateQuestions({ ...questions, candidateName });
      setQuestionsDialogOpen(true);
    } catch (err) {
      console.error('Error generating questions:', err);
      setError(`Failed to generate questions for ${candidateName}: ${err.message}`);
    } finally {
      setLoadingQuestions(prev => ({ ...prev, [candidateId]: false }));
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    return 'Partial Match';
  };

  const CandidateCard = ({ candidate, index }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" component="h3">
              {candidate.name || `Candidate ${index + 1}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              File: {candidate.fileName}
            </Typography>
            {candidate.uploadedAt && (
              <Typography variant="body2" color="text.secondary">
                Uploaded: {new Date(candidate.uploadedAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={`${candidate.matchScore || 0}%`}
              color={getScoreColor(candidate.matchScore || 0)}
              size="small"
            />
            <Tooltip title="Top candidate">
              {index === 0 && candidate.matchScore > 0 && <StarIcon color="warning" />}
            </Tooltip>
          </Box>
        </Box>

        {candidate.error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error processing resume: {candidate.error}
          </Alert>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Match Score: {getScoreLabel(candidate.matchScore || 0)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={candidate.matchScore || 0}
                color={getScoreColor(candidate.matchScore || 0)}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            {candidate.skills && candidate.skills.length > 0 && (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Skills:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {candidate.skills.slice(0, 5).map((skill, idx) => (
                    <Chip
                      key={idx}
                      label={skill}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                  {candidate.skills.length > 5 && (
                    <Chip
                      label={`+${candidate.skills.length - 5} more`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                </Box>
              </Box>
            )}

            {candidate.experience && (
              <Typography variant="body2" color="text.secondary" paragraph>
                Experience: {candidate.experience} years
              </Typography>
            )}

            {candidate.summary && (
              <Typography variant="body2" paragraph>
                {candidate.summary.length > 200
                  ? `${candidate.summary.substring(0, 200)}...`
                  : candidate.summary
                }
              </Typography>
            )}

            {candidate.recommendation && (
              <Box mb={2}>
                <Chip
                  label={`Recommendation: ${candidate.recommendation}`}
                  color={
                    candidate.recommendation === 'Interview' || candidate.recommendation === 'Recommended' ? 'success' :
                    candidate.recommendation === 'Hire' ? 'primary' : 'default'
                  }
                  variant="outlined"
                />
              </Box>
            )}
          </>
        )}

        <Box display="flex" gap={1} mt={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewIcon />}
            onClick={() => handleViewDetails(candidate)}
          >
            View Details
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<QuestionIcon />}
            onClick={() => handleGenerateQuestions(candidate.candidateId || candidate.id || `candidate-${index}`, candidate.name)}
            disabled={loadingQuestions[candidate.candidateId || candidate.id || `candidate-${index}`]}
            color="primary"
          >
            {loadingQuestions[candidate.id || `candidate-${index}`] ? 'Generating...' : 'Interview Questions'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Box display="flex" alignItems="center" mb={2}>
          <PeopleIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            AI Candidate Matching
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Find the best candidates for your job openings using AI-powered analysis of uploaded resumes.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Job
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Choose Job</InputLabel>
              <Select
                value={selectedJobId}
                onChange={handleJobChange}
                label="Choose Job"
                disabled={jobsLoading}
              >
                {jobs.map((job) => (
                  <MenuItem key={job.jobId} value={job.jobId}>
                    {job.title} - {job.department}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedJob && (
              <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    <WorkIcon sx={{ mr: 1, fontSize: 16 }} />
                    Job Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{selectedJob.title}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedJob.department} • {selectedJob.location}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Status: <Chip label={selectedJob.status || 'active'} size="small" color="success" />
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Resume Status
            </Typography>
            
            {selectedJobId && (
              <Box mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Candidates available: <strong>{candidates.length}</strong>
                </Typography>
                
                {resumes.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No resumes uploaded for this job yet.
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {resumes.length} resume{resumes.length !== 1 ? 's' : ''} ready for analysis
                  </Alert>
                )}

                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={handleManageResumes}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Manage Resumes
                </Button>
              </Box>
            )}

            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={findMatches}
              disabled={!selectedJobId || loading || candidates.length === 0}
              fullWidth
              size="large"
            >
              {loading ? 'Analyzing Candidates...' : `Find Best Matches${resumes.length > 0 ? ` (${resumes.length} resumes)` : ''}`}
            </Button>

            {resumes.length === 0 && selectedJobId && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                Upload resumes first to enable candidate matching
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Candidate Analysis Results
            </Typography>

            {loading && (
              <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Analyzing candidates with AI...
                </Typography>
              </Box>
            )}

            {!loading && candidates.length === 0 && !selectedJobId && (
              <Box textAlign="center" py={4}>
                <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a Job to Start
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a job from the dropdown to analyze candidates.
                </Typography>
              </Box>
            )}

            {!loading && candidates.length === 0 && selectedJobId && (
              <Box textAlign="center" py={4}>
                <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Analysis Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {candidates.length === 0 
                    ? 'Upload resumes for this job and click "Find Best Matches" to see AI-powered candidate analysis.'
                    : 'Click "Find Best Matches" to analyze the candidates with AI.'
                  }
                </Typography>
              </Box>
            )}

            {!loading && candidates.length > 0 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Found {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} • Sorted by match score
                  </Typography>
                  <Chip 
                    label={`${candidates.filter(c => !c.error).length} successfully analyzed`} 
                    size="small" 
                    color="success" 
                  />
                </Box>
                {candidates.map((candidate, index) => (
                  <CandidateCard
                    key={candidate.id || index}
                    candidate={candidate}
                    index={index}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Candidate Details Modal */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Candidate Details: {selectedCandidate?.name}
            </Typography>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedCandidate && (
            <Box>
              {/* Match Analysis Section - At the top */}
              <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Match Analysis
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">Score:</Typography>
                      <Chip 
                        label={`${selectedCandidate.matchScore}%`}
                        color={getScoreColor(selectedCandidate.matchScore)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">Recommendation:</Typography>
                      <Chip 
                        label={selectedCandidate.recommendation}
                        color={selectedCandidate.recommendation === 'Recommended' ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Analysis Details */}
                {selectedCandidate.analysis && (
                  <Box mt={2}>
                    {selectedCandidate.analysis.strengths && selectedCandidate.analysis.strengths.length > 0 && (
                      <Box mb={1}>
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                          Strengths:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • {selectedCandidate.analysis.strengths.join('\n• ')}
                        </Typography>
                      </Box>
                    )}
                    {selectedCandidate.analysis.concerns && selectedCandidate.analysis.concerns.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                          Concerns:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • {selectedCandidate.analysis.concerns.join('\n• ')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>

              {/* Contact Information */}
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CopyableField label="Name" value={selectedCandidate.name} />
                </Grid>
                <Grid item xs={6}>
                  <CopyableField label="Email" value={selectedCandidate.email} />
                </Grid>
                <Grid item xs={6}>
                  <CopyableField label="Phone" value={selectedCandidate.phone} />
                </Grid>
                <Grid item xs={6}>
                  <CopyableField label="Location" value={selectedCandidate.location} />
                </Grid>
                <Grid item xs={12}>
                  <CopyableField 
                    label="Experience" 
                    value={`${selectedCandidate.experience || selectedCandidate.experience_years || 'Not available'} years`} 
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Skills */}
              <Typography variant="h6" gutterBottom>
                Skills
              </Typography>
              <CopyableField 
                label="Technical Skills" 
                value={selectedCandidate.skills && Array.isArray(selectedCandidate.skills) 
                  ? selectedCandidate.skills.join(', ') 
                  : 'Not available'
                }
                multiline
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDetails} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interview Questions Modal */}
      <Dialog 
        open={questionsDialogOpen} 
        onClose={handleCloseQuestions}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Interview Questions: {selectedCandidateQuestions?.candidateName}
            </Typography>
            <IconButton onClick={handleCloseQuestions} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedCandidateQuestions && (
            <Box>
              {/* Copy All Questions Button */}
              <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Generated by Amazon Bedrock AI • {selectedCandidateQuestions.metadata?.generationType}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={() => {
                    const allQuestions = [
                      ...(selectedCandidateQuestions.questions?.technical || []),
                      ...(selectedCandidateQuestions.questions?.leadership || []),
                      ...(selectedCandidateQuestions.questions?.personalized || [])
                    ].map((q, i) => `${i + 1}. ${q.question}`).join('\n\n');
                    handleCopyToClipboard(allQuestions, 'All Questions');
                  }}
                >
                  Copy All Questions
                </Button>
              </Box>

              <Grid container spacing={3}>
                {/* Technical Questions */}
                {selectedCandidateQuestions.questions?.technical && selectedCandidateQuestions.questions.technical.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Technical Questions
                    </Typography>
                    {selectedCandidateQuestions.questions.technical.map((question, index) => (
                      <QuestionCard 
                        key={index}
                        question={question}
                        index={index}
                        category="Technical"
                      />
                    ))}
                  </Grid>
                )}

                {/* Leadership Questions */}
                {selectedCandidateQuestions.questions?.leadership && selectedCandidateQuestions.questions.leadership.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Leadership Questions
                    </Typography>
                    {selectedCandidateQuestions.questions.leadership.map((question, index) => (
                      <QuestionCard 
                        key={index}
                        question={question}
                        index={index}
                        category="Leadership"
                      />
                    ))}
                  </Grid>
                )}

                {/* Personalized Questions */}
                {selectedCandidateQuestions.questions?.personalized && selectedCandidateQuestions.questions.personalized.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Personalized Questions
                    </Typography>
                    {selectedCandidateQuestions.questions.personalized.map((question, index) => (
                      <QuestionCard 
                        key={index}
                        question={question}
                        index={index}
                        category="Personalized"
                      />
                    ))}
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseQuestions} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CandidateMatchingPage;
